import { supabase } from "@/integrations/supabase/client";
import { isValidStreamUser } from "./streamUserValidator";
import { isEligibleForSponsoredPayment } from "./streamEligibilityChecker";
import { isEligibleForSponsoredPaymentEnhanced } from "./fraudPrevention/enhancedStreamEligibilityChecker";
import { recordStreamForDuplicatePrevention } from "./fraudPrevention/fraudDetectionService";
import { updateStreamCounts } from "./streamCountUtils";
import { processStreamPayment } from "./streamPaymentProcessor";
import { getGeoIpData, upsertStreamGeoData } from "./geoIpService";

/**
 * Record a valid stream in the database and process any related actions
 * (counting, payments, etc.)
 */
export async function recordValidStream(
  trackId: string,
  userId: string,
  artistId: string,
  duration: number,
  queryClient: any,
) {
  try {
    console.log("🎵 [STREAM_PROCESSING] Starting stream recording:", {
      trackId,
      userId,
      artistId,
      duration,
      timestamp: new Date().toISOString(),
    });

    // Additional safety check - verify user is not suspended (with retry)
    let isSuspended = false;
    let suspensionCheckSucceeded = false;

    for (let attempt = 0; attempt < 3; attempt++) {
      const { data, error } = await supabase.rpc("is_user_suspended", {
        p_user_id: userId,
      });

      if (!error) {
        isSuspended = data === true;
        suspensionCheckSucceeded = true;
        break;
      }

      console.warn(
        `⚠️ [STREAM_PROCESSING] Suspension check attempt ${attempt + 1}/3 failed:`,
        error,
      );
      await new Promise((r) => setTimeout(r, 300 * (attempt + 1)));
    }

    if (!suspensionCheckSucceeded) {
      // FAIL OPEN: Log for monitoring but don't block - fraud system will catch abuse
      console.error(
        "❌ [STREAM_PROCESSING] All suspension check attempts failed, proceeding with caution",
      );
    }

    if (isSuspended) {
      console.log("🚫 [STREAM_PROCESSING] User is suspended, rejecting stream");
      return {
        success: true,
        sponsored: false,
        paymentProcessed: false,
        counted: false,
        eligibilityReason:
          "User is suspended and cannot generate valid streams",
      };
    }

    // Check if this user can generate valid streams (must be a fan and not suspended)
    const isAllowedToGenerateValidStreams = await isValidStreamUser(userId);

    // Artist shouldn't be able to generate streams for their own tracks
    const isSelfStreaming = userId === artistId;

    // Only count the stream as valid if it's from a fan and not self-streaming
    const isValid = isAllowedToGenerateValidStreams && !isSelfStreaming;

    console.log("✅ [STREAM_PROCESSING] Stream validity check:", {
      isAllowedToGenerateValidStreams,
      isSelfStreaming,
      isValid,
      isSuspended,
      userId,
      artistId,
    });

    // Get the user's IP address and geo-location data for tracking
    const geoData = await getGeoIpData();
    const userIP = geoData?.ip || null;
    console.log("🌐 [STREAM_PROCESSING] User IP & geo detected:", {
      userIP: userIP || "unknown",
      country: geoData?.country_code || "unknown",
      city: geoData?.city || "unknown",
      userId,
      trackId,
    });

    // CRITICAL: Run enhanced fraud prevention check ONCE (used for both pre-recording validation and payment eligibility)
    let fraudCheckResult: any = null;
    if (isValid) {
      console.log(
        "🔍 [FRAUD_CHECK] Starting single comprehensive fraud & eligibility check:",
        {
          trackId,
          userId,
          userIP: userIP || "unknown",
          timestamp: new Date().toISOString(),
        },
      );

      fraudCheckResult = await isEligibleForSponsoredPaymentEnhanced(
        trackId,
        userId,
        artistId,
      );

      console.log("🚨 [FRAUD_CHECK] Comprehensive validation result:", {
        eligible: fraudCheckResult.eligible,
        reason: fraudCheckResult.reason,
        duplicateDetected: fraudCheckResult.duplicateCheck?.isDuplicate,
        fraudRiskLevel: fraudCheckResult.fraudDetection?.riskLevel,
        velocityExceeded: fraudCheckResult.velocityCheck?.exceeded,
        trackId,
        userId,
        timestamp: new Date().toISOString(),
      });

      // If duplicate is detected, reject the stream before recording
      if (fraudCheckResult.duplicateCheck?.isDuplicate) {
        console.log(
          "🚫 [DUPLICATE_CHECK] Duplicate stream detected, rejecting:",
          { trackId, userId },
        );
        return {
          success: true,
          sponsored: false,
          paymentProcessed: false,
          counted: false,
          eligibilityReason: `Duplicate stream prevented - allowance type: ${fraudCheckResult.duplicateCheck.allowanceType}`,
        };
      }

      // If velocity limits exceeded, reject the stream
      if (fraudCheckResult.velocityCheck?.exceeded) {
        console.log("🚫 [VELOCITY_CHECK] Stream velocity limit exceeded:", {
          trackId,
          userId,
        });
        return {
          success: true,
          sponsored: false,
          paymentProcessed: false,
          counted: false,
          eligibilityReason: `Velocity limit exceeded: ${fraudCheckResult.velocityCheck.streamsInLastHour} streams in last hour`,
        };
      }

      // Only block critical fraud risks
      if (fraudCheckResult.fraudDetection?.riskLevel === "critical") {
        console.log("🚫 [FRAUD_CHECK] Critical fraud risk, rejecting:", {
          trackId,
          userId,
        });
        return {
          success: true,
          sponsored: false,
          paymentProcessed: false,
          counted: false,
          eligibilityReason: `Stream blocked due to critical fraud risk`,
        };
      }

      if (
        fraudCheckResult.fraudDetection?.riskLevel === "high" ||
        fraudCheckResult.fraudDetection?.riskLevel === "medium"
      ) {
        console.log(
          "⚠️ [FRAUD_CHECK] Elevated fraud risk but allowing with relaxed policy:",
          {
            trackId,
            userId,
            riskLevel: fraudCheckResult.fraudDetection.riskLevel,
          },
        );
      }

      console.log(
        "✅ [VALIDATION_PASSED] Stream passed all pre-recording validations",
      );
    }

    // Only record the stream if it passed all validations (including duplicates)
    const { data: streamData, error } = await supabase
      .from("streams")
      .insert({
        track_id: trackId,
        user_id: userId,
        duration: duration,
        is_valid: isValid,
        ip_address: userIP,
      })
      .select();

    if (error) {
      console.error("❌ [STREAM_PROCESSING] Error recording stream:", error);
      return { success: false, error: error.message };
    }

    console.log("✅ [STREAM_PROCESSING] Stream recorded successfully:", {
      streamData,
      track_id: trackId,
      user_id: userId,
      duration: duration,
      is_valid: isValid,
      ip_address: userIP || "unknown",
    });

    // Upsert geo-location data into stream_ip_analytics (non-blocking)
    if (geoData) {
      upsertStreamGeoData(userId, geoData);
    }

    // Only update counts and process payments if the stream is valid
    if (isValid) {
      console.log(
        "📊 [STREAM_PROCESSING] Stream is valid, updating stream counts",
      );
      const countUpdateResult = await updateStreamCounts(
        trackId,
        artistId,
        queryClient,
      );
      console.log(
        "📈 [STREAM_PROCESSING] Count update result:",
        countUpdateResult,
      );

      // Reuse the fraud check result from pre-recording validation (no second call needed)
      const eligibilityResult = fraudCheckResult;

      console.log("🔍 [PAYMENT_CHECK] Using cached eligibility result:", {
        eligible: eligibilityResult.eligible,
        sponsorId: eligibilityResult.sponsorId,
        reason: eligibilityResult.reason,
        trackId,
        userId,
        artistId,
      });

      // If eligible for sponsored payment, process it
      if (eligibilityResult.eligible && eligibilityResult.sponsorId) {
        console.log("💸 [PAYMENT_PROCESSING] Processing sponsored payment:", {
          artistId,
          sponsorId: eligibilityResult.sponsorId,
          trackId,
          amount_policy: "dynamic via DB (verified artists earn ₦60)",
          timestamp: new Date().toISOString(),
        });

        const paymentProcessed = await processStreamPayment(
          artistId,
          eligibilityResult.sponsorId,
          queryClient,
          trackId,
        );

        console.log("💰 [PAYMENT_PROCESSING] Payment processing result:", {
          paymentProcessed,
          artistId,
          sponsorId: eligibilityResult.sponsorId,
          fraudRiskLevel: eligibilityResult.fraudDetection?.riskLevel,
          deviceTrustScore: eligibilityResult.fraudDetection?.deviceTrustScore,
          timestamp: new Date().toISOString(),
        });

        // Record stream for duplicate prevention (enhanced fraud protection)
        try {
          await recordStreamForDuplicatePrevention(
            userId,
            trackId,
            eligibilityResult.fraudDetection?.deviceTrustScore?.toString() ||
              "unknown",
            `session_${Date.now()}`,
            duration,
          );
          console.log(
            "📝 [FRAUD_PREVENTION] Stream recorded for duplicate prevention",
          );
        } catch (recordError) {
          console.error(
            "❌ [FRAUD_PREVENTION] Failed to record stream for duplicate prevention:",
            recordError,
          );
        }

        // Invalidate relevant queries to refresh stream counts and wallet balances
        if (queryClient) {
          console.log(
            "🔄 [QUERY_INVALIDATION] Invalidating queries after successful payment",
          );
          await queryClient.invalidateQueries({ queryKey: ["tracks"] });
          await queryClient.invalidateQueries({ queryKey: ["chartTracks"] });
          await queryClient.invalidateQueries({ queryKey: ["artistTracks"] });
          await queryClient.invalidateQueries({ queryKey: ["artistProfile"] });
          await queryClient.invalidateQueries({ queryKey: ["artistWallet"] });
        }

        return {
          success: true,
          sponsored: true,
          paymentProcessed: paymentProcessed,
          counted: countUpdateResult,
          eligibilityReason: "Stream eligible for payment",
        };
      } else {
        console.log(
          "❌ [PAYMENT_CHECK] Stream not eligible for payment (enhanced fraud prevention):",
          {
            eligible: eligibilityResult.eligible,
            sponsorId: eligibilityResult.sponsorId,
            reason: eligibilityResult.reason || "Unknown reason",
            fraudRiskLevel: eligibilityResult.fraudDetection?.riskLevel,
            fraudReasons: eligibilityResult.fraudDetection?.fraudReasons,
            deviceTrustScore:
              eligibilityResult.fraudDetection?.deviceTrustScore,
            duplicateDetected: eligibilityResult.duplicateCheck?.isDuplicate,
            velocityExceeded: eligibilityResult.velocityCheck?.exceeded,
            trackId,
            userId,
            artistId,
          },
        );

        // Invalidate relevant queries even if payment wasn't processed
        if (queryClient) {
          await queryClient.invalidateQueries({ queryKey: ["tracks"] });
          await queryClient.invalidateQueries({ queryKey: ["chartTracks"] });
          await queryClient.invalidateQueries({ queryKey: ["artistTracks"] });
          await queryClient.invalidateQueries({ queryKey: ["artistProfile"] });
        }

        return {
          success: true,
          sponsored: false,
          paymentProcessed: false,
          counted: countUpdateResult,
          eligibilityReason:
            eligibilityResult.reason || "Unknown eligibility issue",
        };
      }
    }

    // For non-valid streams, return success but indicate not counted
    return {
      success: true,
      sponsored: false,
      paymentProcessed: false,
      counted: false,
      eligibilityReason: isSuspended
        ? "User is suspended"
        : "Stream not valid (not from fan or self-streaming)",
    };
  } catch (error) {
    console.error(
      "🔥 [STREAM_PROCESSING] Exception in recordValidStream:",
      error,
    );
    return {
      success: false,
      error: error instanceof Error ? error.message : "Unknown error",
    };
  }
}
